// Theme (ARCH §9.5, DESIGN_SYSTEM §14): light lives on `:root`, dark on `[data-theme="dark"]`. The app
// follows the OS by default; a stored override wins. The attribute is set twice on purpose: once by the
// inline script in index.html BEFORE first paint (no light flash for dark users), and again here when the
// app boots, which also starts following OS changes. Both must agree on STORAGE_KEY and the values; a
// test compares them.

export type ThemeName = 'light' | 'dark';
export type ThemePreference = ThemeName | 'system';

export const THEME_STORAGE_KEY = 'sl.theme';
export const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';

export function isThemePreference(value: unknown): value is ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system';
}

/** Storage can be blocked (private windows, strict settings): reading falls back to `system`. */
export function readThemePreference(storage: Pick<Storage, 'getItem'> | null): ThemePreference {
  try {
    const stored = storage?.getItem(THEME_STORAGE_KEY);
    return isThemePreference(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
}

export function resolveTheme(preference: ThemePreference, prefersDark: boolean): ThemeName {
  if (preference === 'system') return prefersDark ? 'dark' : 'light';
  return preference;
}

export function applyTheme(name: ThemeName, root: HTMLElement = document.documentElement): void {
  root.setAttribute('data-theme', name);
}

export interface ThemeController {
  /** The theme on `<html>` right now. */
  get(): ThemeName;
  getPreference(): ThemePreference;
  /** Persists the override (or `system`) and re-applies. */
  setPreference(preference: ThemePreference): void;
  subscribe(listener: () => void): () => void;
  /** Stops following the OS; for tests. */
  dispose(): void;
}

/**
 * Applies the resolved theme now and keeps `<html data-theme>` in step with the OS while the preference is
 * `system`. One instance per app; `ThemeProvider` exposes it to React.
 */
export function installTheme(win: Window = window): ThemeController {
  const root = win.document.documentElement;
  const storage = (() => {
    try {
      return win.localStorage;
    } catch {
      return null;
    }
  })();
  const media = typeof win.matchMedia === 'function' ? win.matchMedia(DARK_MEDIA_QUERY) : null;
  const listeners = new Set<() => void>();
  let preference = readThemePreference(storage);
  let current = resolveTheme(preference, media?.matches ?? false);

  const apply = () => {
    const next = resolveTheme(preference, media?.matches ?? false);
    if (next !== current || root.getAttribute('data-theme') !== next) {
      current = next;
      applyTheme(next, root);
      for (const listener of [...listeners]) listener();
    }
  };
  const onMediaChange = () => {
    if (preference === 'system') apply();
  };
  // `addEventListener` on MediaQueryList: every browser we support; jsdom's stub may lack it.
  media?.addEventListener?.('change', onMediaChange);
  applyTheme(current, root);

  return {
    get: () => current,
    getPreference: () => preference,
    setPreference: (next) => {
      preference = next;
      try {
        // T-076-W11: `'system'` is WRITTEN, not removed. The web used to delete the key where the phone
        // writes the literal (`apps/mobile/src/foundation/theme/appearance.ts:63`), and the two apps share
        // an origin in a `dist/` snapshot — so "follow the OS", chosen on one, read as "nothing stored" on
        // the other, which is the same answer only until one of them gains a different default. Both
        // readers already tolerate both forms (`readThemePreference` falls back to `system`), so writing it
        // is the change that makes them agree, and it is the mobile side that has the harder constraint
        // (React Native storage has no "absent means system" convention to lean on).
        storage?.setItem(THEME_STORAGE_KEY, next);
      } catch {
        /* storage blocked: the override lives for this page load only */
      }
      apply();
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    dispose: () => {
      media?.removeEventListener?.('change', onMediaChange);
      listeners.clear();
    },
  };
}
